/**
 * CurrentPlanCard - Displays current subscription plan with premium design
 */

import React from 'react';
import { Calendar, CreditCard, AlertTriangle, Check, Crown, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { SubscriptionResponse } from '@neokanban/types';
import { format } from 'date-fns';

interface CurrentPlanCardProps {
  subscription: SubscriptionResponse | null;
  onManagePayment?: () => void;
  onCancelSubscription?: () => void;
  onReactivateSubscription?: () => void;
}

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  subscription,
  onManagePayment,
  onCancelSubscription,
  onReactivateSubscription,
}) => {
  if (!subscription) {
    return (
      <Card className="border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <CardHeader className="text-center py-12">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">No Active Subscription</CardTitle>
          <CardDescription className="text-base mt-2">
            You're currently on the free plan. Upgrade to unlock premium features and increase your limits.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const { plan, status, billingPeriod, currentPeriodEnd, trialEnd, cancelAtPeriodEnd } = subscription;
  const price = billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly;
  const isTrialing = status === 'trialing';
  const isCanceled = status === 'canceled';
  const isPastDue = status === 'past_due';
  const isFree = plan.priceMonthly === 0;

  const getStatusBadge = () => {
    if (isTrialing) return <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Trial</Badge>;
    if (isCanceled) return <Badge variant="destructive">Canceled</Badge>;
    if (isPastDue) return <Badge variant="destructive">Past Due</Badge>;
    if (cancelAtPeriodEnd) return <Badge variant="outline" className="border-orange-500 text-orange-600 dark:text-orange-400">Canceling</Badge>;
    return <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Active</Badge>;
  };

  const getPlanGradient = () => {
    if (isFree) return 'from-gray-500 to-gray-700';
    if (plan.name.toLowerCase() === 'pro') return 'from-blue-500 to-purple-600';
    if (plan.name.toLowerCase() === 'business') return 'from-purple-500 to-pink-600';
    if (plan.name.toLowerCase() === 'enterprise') return 'from-orange-500 to-red-600';
    return 'from-blue-500 to-cyan-600';
  };

  return (
    <Card className="border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden">
      {/* Gradient header background */}
      <div className={`h-2 bg-gradient-to-r ${getPlanGradient()}`} />

      <CardHeader className="relative pb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {!isFree && (
                <div className={`p-2 rounded-lg bg-gradient-to-br ${getPlanGradient()} shadow-md`}>
                  <Crown className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <CardTitle className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                  {plan.name} Plan
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  {billingPeriod === 'monthly' ? 'Billed monthly' : 'Billed annually'}
                  {billingPeriod === 'yearly' && !isFree && ' (save 17%)'}
                </CardDescription>
              </div>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Pricing display */}
        {!isFree && (
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-bold text-neutral-900 dark:text-neutral-100">
              ${price.toFixed(2)}
            </span>
            <span className="text-xl text-gray-600 dark:text-gray-400">
              /{billingPeriod === 'monthly' ? 'month' : 'year'}
            </span>
          </div>
        )}

        {/* Trial warning */}
        {isTrialing && trialEnd && (
          <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
            <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-900 dark:text-blue-100">
              Trial ends on <strong>{format(new Date(trialEnd), 'PPP')}</strong>. You'll be charged after the trial period.
            </AlertDescription>
          </Alert>
        )}

        {/* Cancel warning */}
        {cancelAtPeriodEnd && currentPeriodEnd && (
          <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your subscription will be canceled on <strong>{format(new Date(currentPeriodEnd), 'PPP')}</strong>.
            </AlertDescription>
          </Alert>
        )}

        {/* Past due warning */}
        {isPastDue && (
          <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your payment is past due. Please update your payment method to avoid service interruption.
            </AlertDescription>
          </Alert>
        )}

        {/* Plan features */}
        {plan.features && plan.features.length > 0 && (
          <div className="space-y-2 pt-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Plan includes:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {plan.features.slice(0, 6).map((feature: string, idx: number) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className={`mt-0.5 p-0.5 rounded-full bg-gradient-to-br ${getPlanGradient()}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Billing info */}
        {!isCanceled && (
          <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {currentPeriodEnd && (
              <div className="flex items-center gap-2.5 text-sm">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Next billing date</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {format(new Date(currentPeriodEnd), 'PPP')}
                  </div>
                </div>
              </div>
            )}

            {subscription.stripeCustomerId && (
              <div className="flex items-center gap-2.5 text-sm">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <CreditCard className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Payment method</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Card on file</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4">
          {!isCanceled && onManagePayment && subscription.stripeCustomerId && (
            <Button
              variant="outline"
              onClick={onManagePayment}
              className="flex-1 min-w-[180px] border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Manage Payment
            </Button>
          )}

          {!isCanceled && !cancelAtPeriodEnd && onCancelSubscription && subscription.stripeCustomerId && (
            <Button
              variant="ghost"
              onClick={onCancelSubscription}
              className="flex-1 min-w-[180px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              Cancel Subscription
            </Button>
          )}

          {cancelAtPeriodEnd && onReactivateSubscription && subscription.stripeCustomerId && (
            <Button
              variant="default"
              onClick={onReactivateSubscription}
              className={`flex-1 min-w-[180px] bg-gradient-to-r ${getPlanGradient()} hover:opacity-90 text-white border-0`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Reactivate Subscription
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
